import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Card, Form, Button, Row, Col, Spinner, Alert, Table, Modal } from "react-bootstrap";
import PageHeader from "../../shared/PageHeader";
import ImagePreviewModal from "../../shared/ImagePreviewModal";
import FieldRenderer, { FieldSchema } from "./FieldRenderer";
import ProductModal from "./ProductModal";
import { AvailableProduct, SupplierProduct, OrderEditHistory } from "../../../types";
import { buildAppPath } from "../../../config/appBasePath";
import {
  createOrder,
  updateOrder,
  getOrderById,
  getOrderLogs,
  getEditHistory,
} from "../../../services/OrderService";
import { getProductsBySupplier } from "../../../services/ProductService";
import { API_URL } from "../../../services/apiConfig";

import { schemaMap } from "../../../formSchemas";
import Decimal from "decimal.js";

interface SectionSchema {
  title: string;
  fields: FieldSchema[];
}

interface FormSchema {
  type: string;
  title: string;
  sections: SectionSchema[];
  availableProducts?: AvailableProduct[];
}

const computeField = (
  expression: string,
  data: Record<string, any>,
): number => {
  try {
    const tokens = expression.split(/\s*([+-])\s*/);
    let result = parseFloat(data[tokens[0]] || "0") || 0;
    for (let i = 1; i < tokens.length; i += 2) {
      const operator = tokens[i];
      const fieldName = tokens[i + 1];
      const value = parseFloat(data[fieldName] || "0") || 0;
      if (operator === "+") result += value;
      else if (operator === "-") result -= value;
    }
    return Math.round(result * 100) / 100;
  } catch {
    return 0;
  }
};

const DynamicOrderForm: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { type, id } = useParams<{ type?: string; id?: string }>();

  // Determine modes
  const isPrintMode = location.pathname.includes("/orders/print/");
  const isEditMode = location.pathname.includes("/orders/edit/");
  const isViewMode = (!!id && !isEditMode) || isPrintMode;

  const [formData, setFormData] = useState<Record<string, any>>({});
  const [schema, setSchema] = useState<FormSchema | null>(null);
  const [loading, setLoading] = useState(true);
  const [schemaError, setSchemaError] = useState<string | null>(null);

  // Product modal state
  const [showProductModal, setShowProductModal] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [productQuantities, setProductQuantities] = useState<
    Record<string, number>
  >({});
  const [productSearch, setProductSearch] = useState("");

  const [logs, setLogs] = useState<any[]>([]);
  const [existingOrder, setExistingOrder] = useState<any>(null);
  const [editHistory, setEditHistory] = useState<OrderEditHistory[]>([]);
  const [showPreviousVersionModal, setShowPreviousVersionModal] = useState(false);
  const [previousVersionData, setPreviousVersionData] = useState<Record<string, any> | null>(null);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const stateSupplierId = location.state?.supplierId;

        // Load schema from JSON file if type matches, otherwise use standard fallback
        const loadedSchema = type ? schemaMap[type] : null;
        const baseSchema: FormSchema = loadedSchema
          ? { ...loadedSchema, availableProducts: [] }
          : {
              type: "standard-order",
              title: "New Order",
              sections: [
                {
                  title: "Order Details",
                  fields: [
                    {
                      name: "dateRequired",
                      label: "Date Required",
                      type: "date",
                      defaultValue: "",
                    },
                    {
                      name: "notes",
                      label: "Additional Notes",
                      type: "textarea",
                      defaultValue: "",
                    },
                  ],
                },
                {
                  title: "Products",
                  fields: [
                    { name: "products", label: "Products", type: "productList" },
                  ],
                },
              ],
            };

        if ((isViewMode || isEditMode) && id) {
          const order = await getOrderById(parseInt(id, 10));
          if (!order) {
            setSchemaError(`Order not found: ${id}`);
            setLoading(false);
            return;
          }

          try {
            const statusLogs: any = await getOrderLogs(parseInt(id, 10));
            if (statusLogs) setLogs(statusLogs);
          } catch (e) {}

          try {
            const history = await getEditHistory(parseInt(id, 10));
            if (history) setEditHistory(history);
          } catch (e) {}

          if (isEditMode) setExistingOrder(order);

          // Load the correct schema based on saved order type
          const savedSchema = order.type ? schemaMap[order.type] : null;
          const orderSchema: FormSchema = savedSchema
            ? { ...savedSchema, availableProducts: [] }
            : { ...baseSchema };
          // If a view mode order has products, map supplierId
          let viewSupplierId = order.supplierId || 0;
          if (order.formData) {
            try {
              const parsedFormData = JSON.parse(order.formData);
              setFormData(parsedFormData);
              const items = parsedFormData.products || [];
              if (items.length > 0 && items[0].supplierId)
                viewSupplierId = items[0].supplierId;
            } catch {
              setFormData({
                clientId: order.clientId,
                dateRequired: order.dateRequired,
                notes: "",
                products: [],
              });
            }
          }

          // Fetch products for dropdown to display properly
          if (viewSupplierId > 0) {
            const apiProducts = await getProductsBySupplier(viewSupplierId);
            if (apiProducts) {
              orderSchema.availableProducts = apiProducts
                .filter((p: any) => p.availability !== false)
                .map((p: any) => ({
                  code: p.productCode || p.code || "",
                  name: p.name || p.Name || "",
                  size: `${p.weight ?? p.Weight ?? 0} kg`,
                  supplierId: p.supplierId,
                  position: p.position ?? p.Position ?? null,
                }));
            }
          }
          setSchema(orderSchema);
        } else {
          // Create Mode
          if (!stateSupplierId) {
            setSchemaError(
              "No supplier selected. Please pick a supplier first.",
            );
            setLoading(false);
            return;
          }

          const apiProducts = await getProductsBySupplier(stateSupplierId);
          if (apiProducts && Array.isArray(apiProducts)) {
            baseSchema.availableProducts = apiProducts
              .filter((p: any) => p.availability !== false)
              .map((p: any) => ({
                code: p.productCode || p.code || "",
                name: p.name || p.Name || "",
                size: `${p.weight ?? p.Weight ?? 0} kg`,
                supplierId: p.supplierId,
                position: p.position ?? p.Position ?? null,
              }));
          }

          setSchema(baseSchema);

          // Initializing default form data
          const initialData: Record<string, any> = {};
          baseSchema.sections.forEach((section) => {
            section.fields.forEach((field) => {
              initialData[field.name] =
                field.defaultValue !== undefined
                  ? field.defaultValue
                  : field.type === "quantity"
                  ? 1
                  : "";
            });
          });
          initialData["supplierId"] = stateSupplierId; // attach hidden payload prop

          // Get user data for clientId and branch
          try {
            const userData = JSON.parse(
              localStorage.getItem("client_user_data") || "{}",
            );
            if (userData?.clientId) {
              initialData["clientId"] = userData.clientId;
            }
            if (userData?.clientName) {
              initialData["branch"] = userData.clientName;
              initialData["collectFrom"] = userData.clientName;
            }
          } catch (e) {}

          setFormData(initialData);
        }
      } catch (err) {
        console.error(err);
        setSchemaError("Failed to load order page.");
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, isViewMode, isEditMode, location.state]);

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent, saveAsDraft = false) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);

    try {
      const requiredDateValue = formData.dateRequired || formData.requiredDate;

      if (isEditMode && existingOrder && id) {
        // Edit mode — update existing order
        const updatePayload: Record<string, any> = {
          dateRequired: requiredDateValue || existingOrder.dateRequired,
          total: 0,
          formData: JSON.stringify(formData),
        };
        if (saveAsDraft) {
          updatePayload.status = "Draft";
        }

        const result = await updateOrder(parseInt(id, 10), updatePayload);
        if (result) {
          navigate("/orders");
        } else {
          setSubmitError("Failed to update order.");
        }
      } else {
        // Create mode
        const orderPayload = {
          clientId: formData.clientId || 0,
          type: schema?.type || "standard-order",
          dateRequired: requiredDateValue || new Date().toISOString(),
          total: 0,
          status: saveAsDraft ? "Draft" : "Pending",
          formData: JSON.stringify(formData),
          supplierId: formData.supplierId,
        };

        const result = await createOrder(orderPayload as any);
        if (result) {
          navigate("/orders");
        } else {
          setSubmitError(saveAsDraft ? "Failed to save draft." : "Failed to create order.");
        }
      }
    } catch (error) {
      console.error(error);
      setSubmitError("An error occurred while saving the order.");
    } finally {
      setSubmitting(false);
    }
  };

  const addProductsFromModal = () => {
    if (!schema?.availableProducts) return;

    const newProducts = [...(formData.products || [])];
    schema.availableProducts.forEach((product) => {
      const qty = productQuantities[product.code];
      if (qty && qty > 0) {
        // check if already added
        const existing = newProducts.find((p) => p.code === product.code);
        if (existing) {
          existing.quantity = new Decimal(existing.quantity || 0)
            .plus(qty)
            .toDecimalPlaces(3)
            .toNumber();
        } else {
          newProducts.push({ ...product, quantity: qty });
        }
      }
    });

    setFormData((prev) => ({ ...prev, products: newProducts }));
    setProductQuantities({});
    setProductSearch("");
    setShowProductModal(false);
  };

  const handleViewPreviousVersion = (historyRecord: OrderEditHistory) => {
    try {
      if (historyRecord.previousFormData) {
        setPreviousVersionData(JSON.parse(historyRecord.previousFormData));
      } else {
        setPreviousVersionData({});
      }
    } catch {
      setPreviousVersionData({});
    }
    setShowPreviousVersionModal(true);
  };

  // Determine if the existing order is Pending (not Draft)
  const isPendingEdit = isEditMode && existingOrder?.status === "Pending";

  if (loading)
    return (
      <div className="page-wrapper-center">
        <Spinner animation="border" />
      </div>
    );
  if (!schema || schemaError)
    return (
      <div className="page-wrapper">
        <Card>
          <Card.Body>
            <Alert variant="warning">{schemaError}</Alert>
            <Button onClick={() => navigate("/orders")}>Back</Button>
          </Card.Body>
        </Card>
      </div>
    );

  return (
    <div className={`page-wrapper${isPrintMode ? " bg-white p-0" : ""}`}>
      <div
        className={`sub-header-title d-flex justify-content-between align-items-center${
          isPrintMode ? " text-center d-block border-bottom-0" : ""
        }`}
      >
        <span className={isPrintMode ? "h3 d-block mb-0" : ""}>
          {schema.title}
        </span>
      </div>

      <Form onSubmit={handleSubmit}>
        {schema.sections.map((section, sectionIdx) => (
          <div key={sectionIdx}>
            <h5 className="section-title">{section.title}</h5>
            {section.fields.map((field, fieldIdx) => (
              <Row key={fieldIdx} className="mb-2 align-items-center">
                <Col md={2}>
                  <Form.Label className="form-label-custom">
                    {field.label}
                  </Form.Label>
                </Col>
                <Col md={10}>
                  <FieldRenderer
                    field={field}
                    value={formData[field.name] ?? ""}
                    formData={formData}
                    setFormData={setFormData}
                    onShowProductModal={() => setShowProductModal(true)}
                    availableProducts={schema.availableProducts}
                    readOnly={isViewMode && !isEditMode}
                    onShowImagePreview={(src) => setPreviewImage(src)}
                  />
                </Col>
              </Row>
            ))}
          </div>
        ))}
        {submitError && (!isViewMode || isEditMode) && (
          <Alert variant="danger" className="mb-3">
            {submitError}
          </Alert>
        )}
        <div className="mt-4 d-flex gap-2">
          {isEditMode && (
            <>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Update Order"}
              </Button>
              {!isPendingEdit && (
                <Button
                  type="button"
                  variant="outline-secondary"
                  size="sm"
                  disabled={submitting}
                  onClick={(e) => handleSubmit(e as any, true)}
                >
                  {submitting ? "Saving..." : "Save as Draft"}
                </Button>
              )}
            </>
          )}
          {!isViewMode && !isEditMode && (
            <>
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={submitting}
              >
                {submitting ? "Saving..." : "Create Order"}
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                disabled={submitting}
                onClick={(e) => handleSubmit(e as any, true)}
              >
                {submitting ? "Saving..." : "Save as Draft"}
              </Button>
            </>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => navigate("/orders")}
          >
            {isViewMode ? "Back" : "Close"}
          </Button>
        </div>
      </Form>

      <ProductModal
        show={showProductModal}
        onClose={() => setShowProductModal(false)}
        availableProducts={schema.availableProducts || []}
        productQuantities={productQuantities}
        setProductQuantities={setProductQuantities}
        productSearch={productSearch}
        setProductSearch={setProductSearch}
        onAddProducts={addProductsFromModal}
      />

      <ImagePreviewModal
        show={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageSrc={previewImage || ""}
      />

      {/* Order History Section (View & Edit Mode) */}
      {(isViewMode || isEditMode) && (logs.length > 0 || editHistory.length > 0) && (
        <Card className="mt-4 shadow-sm border-0 mb-5">
          <Card.Header className="bg-white py-3 border-bottom-0">
            <h5 className="mb-0 text-dark">Order History</h5>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table className="mb-0 align-middle">
                <thead className="bg-light">
                  <tr>
                    <th className="border-0 ps-4">Activity</th>
                    <th className="border-0">Done by</th>
                    <th className="border-0">Date Time</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log: any) => {
                    let badgeClass = "secondary";
                    const status = log.newStatus || "";
                    const notes = log.notes || "";
                    const isEditLog = notes.includes("edited by");
                    if (status.includes("Confirmed")) badgeClass = "success";
                    else if (status.includes("Production")) badgeClass = "warning text-dark";
                    else if (status.includes("Completed")) badgeClass = "info text-white";
                    else if (status.includes("Dispatched")) badgeClass = "primary";
                    else if (status.includes("Cancelled")) badgeClass = "danger";
                    else if (isEditLog) badgeClass = "info";
                    else if (status.includes("Updated")) badgeClass = "secondary";

                    const matchingEdit = isEditLog ? editHistory.find((eh) => {
                      const editDate = new Date(eh.editedAt).getTime();
                      const logDate = new Date(log.changeDate).getTime();
                      return Math.abs(editDate - logDate) < 5000;
                    }) : null;

                    return (
                      <tr key={log.id}>
                        <td className="ps-4">
                          <span className={`badge bg-${badgeClass} px-3 py-2 rounded-1`}>
                            {isEditLog ? "Edited" : log.newStatus}
                          </span>
                        </td>
                        <td>
                          <div>
                            <div className="d-flex align-items-center">
                              <i className="bi bi-person-fill me-2 text-dark"></i>
                              <span>{log.changedBy}</span>
                            </div>
                            {isEditLog && (
                              <div className="small text-muted mt-1">
                                <i className="bi bi-pencil me-1"></i>
                                {log.changedBy} edited this order
                                {matchingEdit && (
                                  <>
                                    {" — "}
                                    <a
                                      href="#"
                                      className="text-primary text-decoration-none"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        handleViewPreviousVersion(matchingEdit);
                                      }}
                                    >
                                      View previous version
                                    </a>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className="bi bi-calendar-event me-2 text-dark"></i>
                            <span>
                              {new Date(log.changeDate).toLocaleString("en-GB", {
                                weekday: "long", day: "numeric", month: "long", year: "numeric",
                                hour: "2-digit", minute: "2-digit", hour12: true,
                              })}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Previous Version Modal */}
      <Modal show={showPreviousVersionModal} onHide={() => setShowPreviousVersionModal(false)} size="lg" centered>
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title className="h5">
            <i className="bi bi-clock-history me-2"></i>
            Previous Order Version
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {previousVersionData ? (
            <div>
              {Object.entries(previousVersionData).map(([key, value]) => {
                if (value === undefined || value === null || value === "") return null;
                if (typeof value === "string" && value.startsWith("data:image")) return null;

                const label = key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase());

                return (
                  <Row key={key} className="mb-2">
                    <Col md={4}>
                      <span className="fw-semibold text-muted small">{label}</span>
                    </Col>
                    <Col md={8}>
                      {Array.isArray(value) ? (
                        <div>
                          {(value as any[]).map((item: any, idx: number) => (
                            <div key={idx} className="small">
                              {item.name || item.code || JSON.stringify(item)} {item.quantity ? `x ${item.quantity}` : ""}
                            </div>
                          ))}
                        </div>
                      ) : typeof value === "object" ? (
                        <span className="small">{JSON.stringify(value)}</span>
                      ) : (
                        <span className="small">{String(value)}</span>
                      )}
                    </Col>
                  </Row>
                );
              })}
            </div>
          ) : (
            <p className="text-muted text-center">No previous version data available.</p>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <Button variant="secondary" size="sm" onClick={() => setShowPreviousVersionModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default DynamicOrderForm;
